import NavItem from "./NavItem"
import { Link } from "./CustomLink"
import KashVaultNoYearLogo from "../../assets/images/KashVaultNoYearLogo.png"
import globals from "../../constants/globals"
import { adminNavItems, consumerNavItems } from "../../constants/navItems"
import { useAuthStatus } from "../../hooks/useAuthStatus"
import type { IPermission, INavItem } from "../../types/permission"
import useGetMyPermissions from "../../hooks/useGetMyPermissions"
import ResourcesNavItem from "./ResourcesNavItem"
import ReportsNavItems from "./ReportsNavItem"
import { useState } from "react"
import { CommonPaths, ConsumerPaths } from "../../constants/paths"
import {
  CommonPermissions,
  AdminPermissions,
  ConsumerPermissions,
} from "../../constants/permissions"
import BPSAdminNavItems from "./BPSAdminNavItem"
import UsersNavItems from "./UsersNavItems"
import UploaderNavItems from "./UploaderNavItem"
import ClaimsNavItems from "./ClaimsNavItem"
import CommunicationsNavItem from "./CommunicationsNavItem"
import Button from "../styled/Button.styled"

type Props = {
  showNone?: boolean
  forEnrollmentForm?: boolean
  showSignIn?: boolean
}

const { BRAND_LOGO_HEIGHT } = globals

const { SIGNIN_PATH, SIGNOUT_PATH, CONTACTUS_PATH, CURRENT_INCENTIVES_PATH } =
  CommonPaths

const { MY_ACCOUNT_PATH, CLAIM_SALE_PATH } = ConsumerPaths

const { VIEW_RESOURCES, VIEW_CURRENT_INCENTIVES } = CommonPermissions

const {
  VIEW_EDIT_CLAIM,
  VIEW_ENROLLMENT_REPORT,
  VIEW_CLAIM_REPORT,
  VIEW_DEALER_UPLOADER,
  VIEW_ENROLLMENT_LIST,
  VIEW_USERS_LIST,
  VIEW_CHANGE_ADDRESS_REPORT,
  VIEW_INVOICE_ORDER,
  VIEW_CLAIMS,
  VIEW_CLAIM_APPROVAL,
  VIEW_PROGRAM_COMMUNICATIONS,
  VIEW_CURRENT_INCENTIVES_REPORT,
  VIEW_EDIT_VAULT_AMOUNT,
} = AdminPermissions

const { VIEW_MY_ACCOUNT, VIEW_CLAIM_SALE } = ConsumerPermissions
const { IS_ADMIN } = globals

const COLLAPSE = "collapse"

const Header = ({ showNone, showSignIn, forEnrollmentForm }: Props) => {
  const [collapse, setCollapse] = useState(COLLAPSE)
  const { permissions } = useGetMyPermissions()
  const { loggedIn } = useAuthStatus()

  const toggleCollapse = () => setCollapse(() => (collapse ? "" : COLLAPSE))

  return (
    <nav className="navbar navbar-expand-xl bg-white border-bottom">
      <div className="container-fluid">
        <Link className="navbar-brand" to={loggedIn ? "/" : SIGNIN_PATH}>
          <img
            src={KashVaultNoYearLogo}
            height={BRAND_LOGO_HEIGHT}
            className="d-inline-block align-top"
            alt="Program logo"
          />
        </Link>

        {!showNone && (
          <button
            className="navbar-toggler"
            type="button"
            onClick={toggleCollapse}
          >
            <span className="navbar-toggler-icon"></span>
          </button>
        )}

        <div className={`navbar-collapse justify-content-end ${collapse}`}>
          <ul className="navbar-nav" onClick={toggleCollapse}>
            {showNone ? null : showSignIn ? (
              <NavItem to={SIGNIN_PATH} label="Sign In" />
            ) : forEnrollmentForm ? (
              <NavItem to={CONTACTUS_PATH} label="Contact Us" />
            ) : (
              <>
                {loggedIn ? (
                  <>
                    {renderNavItems(
                      IS_ADMIN ? adminNavItems : consumerNavItems,
                      permissions
                    )}
                    {IS_ADMIN ? (
                      <>
                        {permissions.includes(VIEW_EDIT_VAULT_AMOUNT) && (
                          <NavItem
                            to="/edit-vault-amount"
                            label="Edit Vault Amount"
                          />
                        )}
                        {/* Uploader Dropdown */}
                        {[VIEW_DEALER_UPLOADER].some((permission) =>
                          permissions.includes(permission)
                        ) && (
                          <UploaderNavItems toggleCollapse={toggleCollapse} />
                        )}

                        {/* Current Incentives */}
                        {permissions.includes(VIEW_CURRENT_INCENTIVES) && (
                          <NavItem
                            label="Current Incentives"
                            to={CURRENT_INCENTIVES_PATH}
                          />
                        )}

                        {/* Communications Dropdown */}
                        {[VIEW_PROGRAM_COMMUNICATIONS].some((permission) =>
                          permissions.includes(permission)
                        ) && (
                          <CommunicationsNavItem
                            toggleCollapse={toggleCollapse}
                          />
                        )}

                        {/* Claims Dropdown */}
                        {[VIEW_CLAIMS, VIEW_CLAIM_APPROVAL].some((permission) =>
                          permissions.includes(permission)
                        ) && <ClaimsNavItems toggleCollapse={toggleCollapse} />}

                        {/* Reports Dropdown */}
                        {[
                          VIEW_CLAIM_REPORT,
                          VIEW_ENROLLMENT_REPORT,
                          VIEW_CHANGE_ADDRESS_REPORT,
                          VIEW_CURRENT_INCENTIVES_REPORT,
                        ].some((permission) =>
                          permissions.includes(permission)
                        ) && (
                          <ReportsNavItems toggleCollapse={toggleCollapse} />
                        )}

                        {/* Users Dropdown */}
                        {[VIEW_USERS_LIST, VIEW_ENROLLMENT_LIST].some(
                          (permission) => permissions.includes(permission)
                        ) && <UsersNavItems toggleCollapse={toggleCollapse} />}

                        {/* BPS Admin Dropdown */}
                        {[VIEW_EDIT_CLAIM, VIEW_INVOICE_ORDER].some(
                          (permission) => permissions.includes(permission)
                        ) && (
                          <BPSAdminNavItems toggleCollapse={toggleCollapse} />
                        )}
                      </>
                    ) : null}

                    {/* Consumer Side - My Account */}
                    {!IS_ADMIN && permissions.includes(VIEW_MY_ACCOUNT) && (
                      <NavItem to={MY_ACCOUNT_PATH} label="My Account" />
                    )}

                    {/* Resources Dropdown */}
                    {permissions.includes(VIEW_RESOURCES) && (
                      <ResourcesNavItem toggleCollapse={toggleCollapse} />
                    )}

                    <NavItem to={SIGNOUT_PATH} label="Sign Out" />
                  </>
                ) : (
                  <>
                    <NavItem to={SIGNIN_PATH} label="Sign In" />
                  </>
                )}

                {/* Consumer Side - Claim Sale */}
                {!IS_ADMIN && permissions.includes(VIEW_CLAIM_SALE) && (
                  <Link
                    to={CLAIM_SALE_PATH}
                    className="text-decoration-none ms-2 mt-2 mt-md-0"
                  >
                    <Button type="button">Claim a Sale</Button>
                  </Link>
                )}
              </>
            )}
          </ul>
        </div>
      </div>
    </nav>
  )
}

const renderNavItems = (navItems: INavItem[], permissions: IPermission[]) =>
  navItems
    .filter((item: INavItem) => permissions.includes(item.permissionCode))
    .map((item, index) => (
      <NavItem to={item.path} label={item.label} end={item.end} key={index} />
    ))

export default Header
